import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings, isOpeningSoon } from "@/context/SettingsContext";
import { Tag, Layers, HelpCircle, Mail, Inbox, MessageSquare, Activity, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [emailStatus, setEmailStatus] = useState(null);
    const { settings, refresh } = useSettings();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/admin/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
    }, []);

    const toggleMode = async () => {
        const next = isOpeningSoon(settings) ? "store_live" : "opening_soon";
        setSaving(true);
        try {
            await api.put("/admin/settings", { site_mode: next });
            await refresh();
            toast.success("Modus aktualisiert");
        } catch { toast.error("Fehler beim Speichern"); }
        finally { setSaving(false); }
    };

    const cards = [
        { label: "Angebote", value: stats?.offers ?? "–", Icon: Tag, to: "/admin/offers", color: "text-brand-red" },
        { label: "Kategorien", value: stats?.categories ?? "–", Icon: Layers, to: "/admin/categories", color: "text-brand-green" },
        { label: "FAQs", value: stats?.faqs ?? "–", Icon: HelpCircle, to: "/admin/faqs", color: "text-brand-charcoal" },
        { label: "Newsletter", value: stats?.newsletter ?? "–", Icon: Mail, to: "/admin/newsletter", color: "text-brand-green" },
        { label: "Bewerbungen", value: stats?.applications ?? "–", Icon: Inbox, to: "/admin/applications", color: "text-brand-red" },
        { label: "Nachrichten", value: stats?.contact_messages ?? "–", Icon: MessageSquare, to: "/admin/contact", color: "text-brand-charcoal" },
    ];

    return (
        <div className="p-6 md:p-10 max-w-7xl">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-brand-green">Übersicht</div>
                    <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold">Willkommen im Markt-Backoffice.</h1>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={toggleMode} disabled={saving} className="btn-outline-dark" data-testid="toggle-site-mode">
                        <Sparkles className="h-4 w-4 text-brand-green" />
                        Modus: <b className="ml-1">{isOpeningSoon(settings) ? "Eröffnung in Kürze" : "Markt geöffnet"}</b>
                    </button>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
                {cards.map(({ label, value, Icon, to, color }) => (
                    <Link key={label} to={to} data-testid={`stat-${label}`} className="card-clean p-5 flex items-center justify-between hover:-translate-y-0.5 transition">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">{label}</div>
                            <div className="mt-2 font-display text-3xl font-bold">{value}</div>
                        </div>
                        <Icon className={`h-7 w-7 ${color}`} />
                    </Link>
                ))}
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-5">
                <div className="card-clean p-6 lg:col-span-2">
                    <div className="text-xs uppercase tracking-widest text-brand-charcoal/60 flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> System-Status</div>
                    <ul className="mt-4 space-y-3 text-sm">
                        <li className="flex justify-between gap-4"><span>Modus</span><b>{isOpeningSoon(settings) ? "Eröffnung in Kürze" : "Markt geöffnet"}</b></li>
                        <li className="flex justify-between gap-4"><span>Countdown aktiviert</span><b>{settings.countdown_enabled ? "Ja" : "Nein"}</b></li>
                        <li className="flex justify-between gap-4"><span>Eröffnungsdatum</span><b>{settings.opening_date || "—"}</b></li>
                        <li className="flex justify-between gap-4"><span>WhatsApp</span><b>{settings.whatsapp || "—"}</b></li>
                    </ul>
                </div>
                <div className="card-clean p-6">
                    <div className="text-xs uppercase tracking-widest text-brand-charcoal/60 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> E-Mail-Benachrichtigungen</div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: emailStatus?.enabled ? "#163A2E" : "#EFEAE0", color: emailStatus?.enabled ? "#fff" : "#111" }}>
                        {emailStatus?.enabled ? "Aktiv" : "Deaktiviert"}
                    </div>
                    {emailStatus?.enabled ? (
                        <p className="mt-3 text-xs text-brand-charcoal/65">Provider: <b>{emailStatus.provider}</b><br />Empfänger: <b>{emailStatus.to}</b></p>
                    ) : (
                        <p className="mt-3 text-xs text-brand-charcoal/65">Setze <code>EMAIL_PROVIDER</code> (resend/sendgrid) und den passenden API-Key in der .env, um Benachrichtigungen zu aktivieren.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
